import { inject, Injectable } from '@angular/core';
import { Auth, authState, User } from '@angular/fire/auth';
import { catchError, map, Observable, of, ReplaySubject } from 'rxjs';
import { PLATFORM_TOKEN, PlatformProvider } from '../tokens';
import { AuthStateChange, FirebaseAuthentication, GetCurrentUserResult } from '@capacitor-firebase/authentication';

@Injectable({ providedIn: 'root' })
export class AuthClientService {
  private readonly auth: Auth = inject(Auth);
  private readonly platformProvider: PlatformProvider = inject(PLATFORM_TOKEN);

  private readonly authStateNativeSubject$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  private readonly isNative: boolean = this.platformProvider.isNativeRun;

  constructor() {
    if (this.isNative) {
      this.initNativeAuthListener();
    }
  }

  get authClient(): Auth {
    return this.auth;
  }

  get isAuthed$(): Observable<boolean> {
    if (this.isNative) {
      return this.authStateNativeSubject$.pipe(catchError(() => of(false)));
    }

    return authState(this.auth).pipe(
      map((user: User | null) => !!user),
      catchError(() => of(false))
    );
  }

  private initNativeAuthListener(): void {
    FirebaseAuthentication.addListener('authStateChange', (change: AuthStateChange) => {
      this.authStateNativeSubject$.next(!!change.user);
    });

    FirebaseAuthentication.getCurrentUser()
      .then((result: GetCurrentUserResult) => {
        this.authStateNativeSubject$.next(!!result.user);
      })
      .catch(() => {
        this.authStateNativeSubject$.next(false);
      });
  }
}
